import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useEffect, useState } from 'react';
import {
  Box,
  DialogContent,
  DialogTitle,
  Link,
  Tooltip,
  Typography,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { makeStyles } from '@material-ui/core/styles';
import {
  ChevronLeft, ChevronRight, Create as CreateIcon, Link as LinkIcon, Label as LabelIcon,
} from '@material-ui/icons';
import classNames from 'classnames';
import Button from 'Components/UI/Button';
import Dialog from 'Components/UI/Dialog';
import { supportedPreviewExtensions } from 'Consts/EnumeratedValues';
import {
  currentFile as currentFileConst,
  files as filesConst,
} from 'Consts/Queries';
import { secureFilesUri as clientSecureFilesUri } from 'Consts/clientUris';
import { householdSecureFilesUri as advisorSecureFilesUri } from 'Consts/advisorUris';
import { QUERY_REFRESH_CURRENT_FOLDER } from 'Consts/File';
import PropTypes from 'prop-types';
import { addQuery, QueryVal, removeQuery } from 'Helpers/Query';
import modalConsts from 'Consts/ModalNames';
import useUserContext from 'Context/UserContext';
import {
  displayErrorMessage,
  displaySuccess,
} from 'Helpers/FacetNotificationManager';
import { copyToClipboard, partition } from 'Helpers/Utils';
import useMobileLayout from 'Hooks/useMobileLayout';
import Sentry from 'Integrations/Sentry';
import { getHidden } from 'Helpers/Meta';
import fr from 'facet-rest';
import FileNameEditor from './FileNameEditor';
import { DocumentTypeEditor } from './DocumentTypeEditor';
import Preview from './Preview';
import useSimpleHousehold from '../../../Hooks/useSimpleHousehold';

function isPreviewable(filename) {
  const extArray = filename.split('.');
  const ext = extArray[extArray.length - 1];

  return supportedPreviewExtensions.indexOf(ext.toLowerCase()) > -1;
}

const useStyles = makeStyles(theme => ({
  disableLinkStyle: {
    fontSize: '1em',
    color: 'white',
    '&:hover': {
      textDecoration: 'none',
      color: 'inherit',
    },
    underline: {
      '&:before': {
        borderBottom: 'none',
        color: 'inherit',
      },
      '&:after': {
        borderBottom: 'none',
        color: 'inherit',
      },
    },
  },
  title: {
    alignSelf: 'center',
    flexGrow: 1,
    flexBasis: '60%',
  },
  paper: {
    maxWidth: theme.dimensions.xxl,
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.facet.primaryBlue,
    margin: '0 auto',
  },
  footer: {
    padding: theme.spacing(0.5),
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.common.white,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  footerMobile: {
    padding: theme.spacing(0.5),
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.common.white,
    justifyContent: 'center',
  },
  mainFooterActionsMobile: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  file: {
    display: 'flex',
    justifyContent: 'center',
    color: theme.palette.common.white,
    flexGrow: 1,
    overflowY: 'auto',
  },
  backNextContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2, 2),
  },
  download: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.primary.dark,
  },
  downloadButton: {
    marginTop: theme.spacing(2),
  },
  fullWidth: {
    padding: 0, // <-- Removes default padding from Paper (allowing content to stretch full-width of the Dialog)
  },
  fileDialogContent: {
    height: '80vh', // <-- ** TODO ** : Look into why the DialogContent is not adhering to MaxHeight rule.
  },
  footerButtonSpacer: {
    marginRight: theme.spacing(2),
  },
  iframe: {},
}));

const FileViewerModal = () => {
  const { getFilestoreResource, updateFile } = useSwaggerlessContext();
  const classes = useStyles();
  const [href, setHref] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileResource, setFileResource] = useState('');
  const [filePreviewList, setFilePreviewList] = useState([]);
  const [currentFile, setCurrentFile] = useState(QueryVal(currentFileConst));
  const [openNameEditor, setOpenNameEditor] = useState(false);
  const [openDocumentTypeEditor, setOpenDocumentTypeEditor] = useState(false);
  const [householdId, setHouseholdId] = useState();
  const isMobile = useMobileLayout();
  const { isAgent } = useUserContext();
  const { householdResource } = useSimpleHousehold();

  const presentsAsDownloadable = fileResource?.relationships ? !!fileResource.relationships[fr.relationships.presentsAsDownloadable.name] : false;

  const splitFilename = fileName.split('.');
  const fileExtension = splitFilename[splitFilename.length - 1];
  const sanitizedFilename = fileName.replace(`.${fileExtension}`, '');


  // removing the 'modal' query triggers our modal router to close the modal
  const handleClose = () => {
    removeQuery(currentFileConst, filesConst, modalConsts.modal);
  };

  useEffect(() => {
    setCurrentFile(QueryVal(currentFileConst));
    let files = QueryVal(filesConst);
    if (files) {
      // This could be ?files=1,2&files=3 or it could be ?files=1,2,3 for example.
      // The former would become ['1,2','3'], while the latter would become '1,2,3'. We need to handle both.
      if (!Array.isArray(files)) {
        files = [files];
      }
      const [validQueryParams, invalidQueryParams] = partition(files, (val) => typeof val === 'string');
      if (invalidQueryParams.length > 0) {
        Sentry.createSentryErrorLog(
          new Error(`FileViewer encountered an error with the files query parameter parsing of ${window.location.toString()}`),
        );
      }
      const temp = validQueryParams.map(commaSeparatedFiles => commaSeparatedFiles.split(',')).flat();
      setFilePreviewList(temp);
    }
  }, []);

  useEffect(() => {
    if (householdResource) {
      setHouseholdId(householdResource.id());
    }
  }, [householdResource]);

  useEffect(() => {
    if (householdId) {
      getFilestoreResource(householdId, currentFile)
        .then((resp) => {
          setHref(resp.data[0].links.related.href);
          setFileName(resp.data[0].attributes.name);
          setFileResource(resp.data[0]);
        })
        .catch((e) => {
          setFileName('');
          setHref('');
          displayErrorMessage(e, 'Failed to fetch file');
        });
    }
  }, [currentFile, householdId]);

  function moveRight() {
    const index = filePreviewList.indexOf(currentFile);
    setCurrentFile(filePreviewList[index + 1]);
    addQuery({ [currentFileConst]: filePreviewList[index + 1] });
  }

  function moveLeft() {
    const index = filePreviewList.indexOf(currentFile);
    setCurrentFile(filePreviewList[index - 1]);
    addQuery({ [currentFileConst]: filePreviewList[index - 1] });
  }

  function handleNameEdit(shouldUpdate, name) {
    if (shouldUpdate) {
      if (householdId) {
        updateFile(householdId, currentFile, {
          ...fileResource,
          attributes: {
            ...fileResource.attributes,
            name,
          },
        })
          .then(() => {
            setFileName(name);
            setOpenNameEditor(false);
            addQuery({ [QUERY_REFRESH_CURRENT_FOLDER]: true });
            displaySuccess('Updated file name.');
          })
          .catch(e => displayErrorMessage(e, 'Failed to update file name.'));
      }
    }
  }

  function handleDocumentTypeUpdate({ documentTypeForML }) {
    return updateFile(householdId, currentFile, {
      ...fileResource,
      attributes: {
        ...fileResource.attributes,
        documentTypeForML,
      },
    })
      .then((res) => {
        if (res === null) {
          // This was an error, do not do any of the nice things we do on success
          return;
        }
        setOpenDocumentTypeEditor(false);
        addQuery({ [QUERY_REFRESH_CURRENT_FOLDER]: true });
        handleClose();
        displaySuccess('Updated document type.');
      });
  }

  const FileContent = (
    <Box className={classes.file}>
      <Preview
        classes={classes}
        fileName={fileName}
        href={href}
        presentsAsDownloadable={presentsAsDownloadable}
      />
    </Box>
  );

  const FileFooter = () => (
    <Box className={isMobile ? classes.footerMobile : classes.footer}>
      <div className={isMobile && classes.mainFooterActionsMobile}>
        {presentsAsDownloadable ? (
          <Tooltip
            title={
            isPreviewable(fileName, fileResource)
              ? `Open ${fileName} in new Window`
              : `Download ${fileName}`
          }
          >
            <Link
              component={Button}
              href={href}
              download={fileName}
              target="_blank"
              className={classNames(classes.disableLinkStyle, {
                [classes.footerButtonSpacer]: !isMobile,
              })}
              startIcon={<GetAppIcon />}
            >
              {'Download'}
            </Link>
          </Tooltip>
        ) : null}
        <Tooltip title="Rename File" aria-label="rename file">
          <Button
            component={Button}
            onClick={() => {
              setOpenNameEditor(true);
            }}
            className={classNames(classes.disableLinkStyle, {
              [classes.footerButtonSpacer]: !isMobile,
            })}
            startIcon={<CreateIcon />}
          >
            {'Rename'}
          </Button>
        </Tooltip>
        {isAgent && (
          <Tooltip title="Copy a link to this secure file for clients" aria-label="copy link">
            <Button
              component={Button}
              onClick={() => {
                const windowUrl = new URL(window.location.href);
                const port = windowUrl.port ? `:${windowUrl.port}` : '';

                const searchParams = new URLSearchParams(window.location.search);
                searchParams.delete('householdId');
                const url = `${window.location.protocol}//${window.location.hostname}${port}${clientSecureFilesUri().replace('householdId=', '')}${searchParams.toString()}`;

                copyToClipboard(url);
              }}
              className={classNames(classes.disableLinkStyle, {
                [classes.footerButtonSpacer]: !isMobile,
              })}
              startIcon={<LinkIcon />}
            >
              {'Copy Link For Client'}
            </Button>
          </Tooltip>
        )}
        {
          fileResource?.meta?.template && !getHidden(fileResource.meta.template, 'documentTypeForML')
            ? (
              <Button
                component={Button}
                onClick={() => {
                  setOpenDocumentTypeEditor(true);
                }}
                className={classNames(classes.disableLinkStyle, {
                  [classes.footerButtonSpacer]: !isMobile,
                })}
                startIcon={<LabelIcon />}
              >
                Specify type
              </Button>
            )
            : null
        }
      </div>
      <Link
        href={
          isAgent
            ? advisorSecureFilesUri(householdId)
            : clientSecureFilesUri(householdId)
        }
        component={Button}
        underline="none"
        className={classNames(classes.actions, classes.disableLinkStyle)}
        endIcon={<ChevronRight />}
      >
        {'Go to Files'}
      </Link>
    </Box>
  );

  return (
    <>
      <FileNameEditor
        fileName={sanitizedFilename}
        fileExtension={fileExtension}
        handleUpdate={handleNameEdit}
        open={openNameEditor}
        handleClose={() => setOpenNameEditor(false)}
      />
      <DocumentTypeEditor
        resource={fileResource}
        handleUpdate={handleDocumentTypeUpdate}
        open={openDocumentTypeEditor}
        onClose={() => setOpenDocumentTypeEditor(false)}
      />
      <Dialog open fullScreen={isMobile} fullWidth={!isMobile} maxWidth="lg" onClose={handleClose} disableEnforceFocus>
        <DialogTitle>
          <Typography component="div" variant="h4">
            {fileName}
          </Typography>
        </DialogTitle>
        <div className={classes.backNextContainer}>
          <Button
            variant="contained"
            disableElevation
            onClick={() => moveLeft()}
            disabled={filePreviewList.indexOf(currentFile) === 0}
            color="primary"
            className={classes.button}
            startIcon={<ChevronLeft />}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => moveRight()}
            disabled={
              filePreviewList.indexOf(currentFile)
              === filePreviewList.length - 1
            }
            color="primary"
            className={classes.button}
            endIcon={<ChevronRight />}
          >
            Next
          </Button>
        </div>
        <DialogContent
          className={classNames(classes.paper, classes.fullWidth, {
            [classes.fileDialogContent]: !isMobile,
          })}
        >
          {FileContent}
          <FileFooter />
        </DialogContent>
      </Dialog>
    </>
  );
};

FileViewerModal.propTypes = {
  userRole: PropTypes.string.isRequired,
};

export default FileViewerModal;
